import { useState, useEffect } from 'react'
import '../safetyRules/safetyRules.css'
import { useLanguage } from '../../context/LanguageContext'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'

const Quiz = () => {
  const {
    language,
    visitorData,
    sessionId,
    tenantSlug,
    quizAnswers, setQuizAnswers,
    setQuizPassed,
    setQrBase64,
    checkinSubmitted, setCheckinSubmitted,
    setAlreadyCheckedIn,
    safetyRulesReadAt,
    setQuizCompletedAt,
    safetyRulesForPdf,
    goNext, goBack,
    allTranslations,
  } = useLanguage()

  const t   = allTranslations[language] || allTranslations['en']
  const tDe = allTranslations['de']

  const [dbQuestions,   setDbQuestions]   = useState(null)
  const [dbQuestionsDe, setDbQuestionsDe] = useState(null)
  const [loadingQ,      setLoadingQ]      = useState(true)
  const [checked,       setChecked]       = useState(false)
  const [allCorrect,    setAllCorrect]    = useState(false)
  const [saving,        setSaving]        = useState(false)
  const [saveError,     setSaveError]     = useState(null)

  useEffect(() => {
    if (!tenantSlug) return

    fetch(`${BACKEND_URL}/api/checkin/quiz-questions?lang=${language}`, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.questions?.length > 0) setDbQuestions(data.questions) })
      .catch(() => {})

    fetch(`${BACKEND_URL}/api/checkin/quiz-questions?lang=de`, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.questions?.length > 0) setDbQuestionsDe(data.questions) })
      .catch(() => {})
      .finally(() => setLoadingQ(false))
  }, [tenantSlug, language])

  const questions   = dbQuestions   || t.questions   || []
  const questionsDe = dbQuestionsDe || tDe.questions || []

  const handleSelectAnswer = (qIdx, oIdx) => {
    if (checked || checkinSubmitted) return
    setQuizAnswers(prev => ({ ...prev, [qIdx]: oIdx }))
  }

  const handleCheckAnswers = async () => {
    if (checkinSubmitted) { goNext(); return }

    const correct = questions.every((q, i) => quizAnswers[i] === q.correct)
    setChecked(true)
    setAllCorrect(correct)

    if (correct) {
      if (!tenantSlug) {
        setSaveError(t.no_tenant_error)
        setChecked(false); setAllCorrect(false)
        return
      }

      const completedAt = new Date().toISOString()
      setQuizCompletedAt(completedAt)
      setSaving(true)
      setSaveError(null)

      try {
        const quizResultForPdf = questionsDe.map((q, i) => ({
          question:      q.question,
          options:       q.options,
          visitorAnswer: quizAnswers[i],
          correctAnswer: q.correct,
        }))

        const response = await fetch(`${BACKEND_URL}/api/checkin`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': tenantSlug },
          body: JSON.stringify({
            sessionId,
            visitorData,
            safetyRulesReadAt,
            quizCompletedAt:   completedAt,
            quizResultForPdf,
            safetyRulesForPdf: safetyRulesForPdf || tDe.safety_rules,
            language,
          }),
        })

        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Request failed')

        setQrBase64(data.qrBase64)
        setQuizPassed(true)
        setCheckinSubmitted(true)
        setAlreadyCheckedIn(data.alreadyCheckedIn)

        setTimeout(() => goNext(), 800)

      } catch (err) {
        console.error('Checkin error:', err)
        setSaveError(err.message)
        setChecked(false); setAllCorrect(false)
        setQuizCompletedAt(null)
      } finally {
        setSaving(false)
      }
    }
  }

  const handleRetry = () => {
    setQuizAnswers({})
    setChecked(false)
    setAllCorrect(false)
    setSaveError(null)
  }

  const getOptionClass = (qIdx, oIdx) => {
    const base = 'viGuard__quiz-option'
    if (!checked) return quizAnswers[qIdx] === oIdx ? `${base} selected` : base
    if (oIdx === questions[qIdx].correct) return `${base} correct`
    if (quizAnswers[qIdx] === oIdx) return `${base} wrong`
    return base
  }

  return (
    <div className="viGuard__safetyRules">
      <div className="viGuard__quiz-content">
        <h1 className="gradient__text">{t.quiz_title}</h1>
        <p className="subtitle">{t.quiz_subtitle}</p>

        {loadingQ ? (
          <p className="page-loading-text">{t.loading}</p>
        ) : (
          questions.map((q, qIdx) => (
            <div className="viGuard__quiz-question" key={qIdx}>
              <h3>{qIdx + 1}. {q.question}</h3>
              <div className="viGuard__quiz-options">
                {q.options.map((opt, oIdx) => (
                  <button
                    key={oIdx}
                    className={getOptionClass(qIdx, oIdx)}
                    onClick={() => handleSelectAnswer(qIdx, oIdx)}
                    type="button"
                  >
                    <input type="radio" readOnly checked={quizAnswers[qIdx] === oIdx} tabIndex={-1} />
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}

        {checked && !allCorrect && <div className="viGuard__quiz-error">{t.quiz_wrong}</div>}
        {saveError && <div className="viGuard__quiz-error">{saveError}</div>}

        <div className="viGuard__quiz-actions">
          {checkinSubmitted ? (
            <button className="btn-primary" type="button" onClick={goNext}>
              {t.qr_title}
            </button>
          ) : (
            (!checked || !allCorrect) && (
              <button
                className="btn-primary"
                type="button"
                onClick={checked ? handleRetry : handleCheckAnswers}
                disabled={saving || loadingQ || questions.some((_, i) => quizAnswers[i] === undefined)}
              >
                {saving ? t.saving : (checked && !allCorrect ? t.quiz_retry : t.quiz_submit)}
              </button>
            )
          )}
          <button className="btn-ghost" type="button" onClick={goBack}>
            {t.quiz_back}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Quiz
